import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

interface AuthContextType {
  isAdmin: boolean;
  isLoading: boolean;
  user: {
    role?: string;
    userId?: string;
    name?: string;
    email?: string;
  } | null;
}

const AuthContext = createContext<AuthContextType>({
  isAdmin: false,
  isLoading: true,
  user: null,
});

export function useAuth() {
  return useContext(AuthContext);
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { user: auth0User, getAccessTokenSilently, isLoading: auth0Loading } = useAuth0();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<{
    role?: string;
    userId?: string;
    name?: string;
    email?: string;
  } | null>(null);

  useEffect(() => {
    const updatePermissions = async () => {
      if (!auth0User) {
        setIsAdmin(false);
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const parts = token.split('.');
        const payload = JSON.parse(atob(parts[1]));
        
        if (payload.permissions) {
          // Update user permissions
          auth0User['https://midwestrailbirds.com/permissions'] = payload.permissions;
          const hasAdmin = payload.permissions.includes('admin');
          setIsAdmin(hasAdmin);
          
          // Set user object with role
          setUser({
            role: hasAdmin ? 'admin' : 'user',
            userId: auth0User.sub,
            name: auth0User.name,
            email: auth0User.email
          });
        }
      } catch (e) {
        setIsAdmin(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (!auth0Loading) {
      updatePermissions();
    }
  }, [auth0User, getAccessTokenSilently, auth0Loading]);

  return (
    <AuthContext.Provider value={{ isAdmin, isLoading, user }}>
      {children}
    </AuthContext.Provider>
  );
}